import {Type, plainToInstance} from "class-transformer";
import {LinkTarget, type LinkTargetKind} from "./link-target";
import {ExternalLinkTarget} from "./external-link-target";
import {KnowledgeObjectLinkTarget} from "./knowledge-object-link-target";
import {GimmickLinkTarget} from "./gimmick-link-target";
import {UnknownLinkTarget} from "./unknown-link-target";
import {LinkTileHelper} from "../../../helpers/link-tile.helper";
import {isGimmickName} from "../../gimmicks/gimmick-registry";

/** The one canonical list of known `LinkTargetKind`s — feeds both the
 *  `class-transformer` discriminator config below and (from
 *  `tiles/tile-registry.ts`, kept in a SEPARATE file, see that module's own
 *  comment) the tile-component lookup, so an invariant test can assert the two
 *  never drift apart. */
export const LINK_TARGET_KINDS: readonly LinkTargetKind[] = ['external', 'knowledge-object', 'gimmick'] as const;

/** Carrier class for the one `@Type()`/discriminator call below — `LinkTile`
 *  itself stays a plain interface (not a `class-transformer` DTO), this class
 *  exists only so `plainToInstance` has somewhere to attach the discriminator
 *  config to. Never exposed/constructed outside this module. */
class LinkTargetHolder {

    @Type(() => LinkTarget, {
        discriminator: {
            property: 'kind',
            subTypes: [
                {value: ExternalLinkTarget, name: 'external'},
                {value: KnowledgeObjectLinkTarget, name: 'knowledge-object'},
                {value: GimmickLinkTarget, name: 'gimmick'},
            ],
        },
    })
    public target?: LinkTarget;

}

function isValidExternal(target: LinkTarget): target is ExternalLinkTarget {
    return target instanceof ExternalLinkTarget && typeof target.url === 'string' && target.url !== '';
}

function isValidKnowledgeObject(target: LinkTarget): target is KnowledgeObjectLinkTarget {
    return target instanceof KnowledgeObjectLinkTarget
        && typeof target.uid === 'string' && target.uid !== ''
        && ['whiteboard', 'document', 'image'].includes(target.knowledgeObjectType);
}

/** A known gimmick with `options` as a plain object — `null`, an array or a missing name make the target
 *  unknown (feature plan 0071), which `dashboard-creator` mounts as a plain tile. */
function isValidGimmick(target: LinkTarget): target is GimmickLinkTarget {
    if (!(target instanceof GimmickLinkTarget) || !isGimmickName(target.gimmick)) {
        return false;
    }
    const options: unknown = target.options;
    return typeof options === 'object' && options !== null && !Array.isArray(options);
}

export class LinkTargetSerializer {

    /**
     * Never returns `null` — an unrecognized `kind` (class-transformer's own
     * discriminator fallback: a bare `LinkTarget` instance carrying whatever
     * raw fields were present, verified empirically against
     * `class-transformer@0.5.1`) or a known `kind` that fails its own field
     * validation both become an `UnknownLinkTarget` built from the ORIGINAL raw
     * JSON (not the transformed fallback instance, to guarantee byte-for-byte
     * fidelity regardless of class-transformer's exact copy semantics).
     */
    public static fromJSON(json: Record<string, unknown>): LinkTarget {
        // `plainToInstance` mutates its input in place — it deletes the
        // discriminator property (`kind`) from the source object once it has
        // read it, even when no subtype matches (verified empirically against
        // class-transformer@0.5.1). Passing a shallow copy keeps the ORIGINAL
        // `json` intact for the `UnknownLinkTarget(json)` fallback below.
        const holder = plainToInstance(LinkTargetHolder, {target: {...json}});
        const target = holder.target;

        if (target && isValidExternal(target)) {
            // Re-normalized/scheme-guarded here too (review Code-Lauf 01,
            // MAJOR), not just on the write path (`LinkTileHelper.
            // normalizeTarget`, called from `createTile`/`applyDraft`): a
            // `target.url` revived from storage may predate that guard, or
            // have been hand-edited in localStorage. Refused (e.g.
            // `javascript:`) → the SAME lossless `UnknownLinkTarget(json)`
            // fallback as an unrecognized `kind`, not a silent drop — the raw
            // JSON survives a save/reload cycle unchanged until the tile is
            // re-edited.
            const normalizedUrl = LinkTileHelper.normalizeUrl(target.url);
            return normalizedUrl ? new ExternalLinkTarget(normalizedUrl) : new UnknownLinkTarget(json);
        }
        if (target && isValidKnowledgeObject(target)) return target;
        if (target && isValidGimmick(target)) return new GimmickLinkTarget(target.gimmick, {...target.options});

        return new UnknownLinkTarget(json);
    }

}
